/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {GridRenderCellParams} from '@mui/x-data-grid';
import React from 'react';

/**
 * A React component that renders a cell with its content aligned to the top. This is primarily useful if the height of
 * a row is dynamic (e.g., when using the expandable chip cell).
 *
 * @param {object} props - The props object.
 * @param {GridRenderCellParams<any, any, any, any>} props.params - The parameters provided by the Grid cell.
 * @param {JSX.Element} props.children - The content to be rendered inside the cell.
 * @param {React.CSSProperties} [props.style] - Optional additional inline styles to apply to the cell.
 * @returns {JSX.Element} A React element representing a top-aligned cell.
 */
export const TopAlignedCell = ({
  params: _params,
  children,
  style,
}: {
  params: GridRenderCellParams<any, any, any, any>;
  children: JSX.Element;
  style?: React.CSSProperties;
}): JSX.Element => {
  return (
    <div
      style={{
        paddingTop: 8,
        paddingBottom: 8,
        alignSelf: 'flex-start',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/**
 * A higher-order function that creates a React component for rendering a top-aligned cell with a fixed height. This
 * function is intended to be used with mui-data-grid's `renderCell` ColDef parameter.
 *
 * @param {number} fixedHeight - The fixed height in pixels for the cell.
 * @param extraStyle - Optional additional inline styles to apply to the cell.
 * @returns {Function} A function that takes GridRenderCellParams and returns a React element representing a top-aligned
 * cell with fixed height.
 */
export const RenderTopAlignedFixedHeightCell = (
  fixedHeight: number,
  extraStyle?: React.CSSProperties,
): ((params: GridRenderCellParams<any, any, any>) => React.ReactNode) => {
  return (params: GridRenderCellParams<any, any, any, any>) => (
    <TopAlignedCell
      params={params}
      style={{
        height: `${fixedHeight}px`,
        lineHeight: `${fixedHeight}px`,
        padding: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...extraStyle,
      }}
    >
      {params.value}
    </TopAlignedCell>
  );
};
